import { useEffect, useState } from 'react'
import { useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { TokenUsageCopy, TokenUsagePerson, TokenUsageReport } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import MappedPersonEditor from './MappedPersonEditor'
import UsageChannelIcon from './UsageChannelIcon'

interface Props {
  workerId: string
  person: TokenUsagePerson | null
  discoveredPeople: TokenUsagePerson[]
  canonicalPeople: TokenUsageReport['canonical_people']
  copy: TokenUsageCopy
  onClose: () => void
}

const CREATE_NEW = 'create-new'
const CANONICAL_PREFIX = 'canonical:'
const IDENTITY_PREFIX = 'identity:'

export default function PersonAliasDialog({ workerId, person, discoveredPeople, canonicalPeople, copy, onClose }: Props) {
  const mapped = Boolean(person?.person_id)
  const [targetValue, setTargetValue] = useState(CREATE_NEW)
  const { data, setData, post, processing, reset } = useForm({
    person: {
      name: '',
      channel: '',
      person_ref_hash: '',
      person_ref_type: '',
      token_usage_person_id: '',
      target_channel: '',
      target_person_ref_hash: '',
      target_person_ref_type: '',
    },
  })
  const discoveredCandidates = discoveredPeople.filter((candidate) => (
    !candidate.person_id &&
    candidate.key !== person?.key &&
    candidate.identifiers.length > 0
  ))
  const hasGroupingCandidates = canonicalPeople.length > 0 || discoveredCandidates.length > 0

  useEffect(() => {
    if (!person) return
    const identifier = person.identifiers[0]
    setTargetValue(person.person_id ? `${CANONICAL_PREFIX}${person.person_id}` : CREATE_NEW)
    setData('person', {
      name: person.name,
      channel: identifier?.channel ?? '',
      person_ref_hash: identifier?.person_ref_hash ?? '',
      person_ref_type: identifier?.person_ref_type ?? '',
      token_usage_person_id: person.person_id ?? '',
      target_channel: '',
      target_person_ref_hash: '',
      target_person_ref_type: '',
    })
  }, [person, setData])

  const close = () => {
    reset()
    onClose()
  }

  const submit = () => {
    if (!person) return
    post(`/dashboard/digital_workers/${workerId}/token_usage_people`, { preserveScroll: true, onSuccess: close })
  }

  const handleTargetChange = (value: string) => {
    setTargetValue(value)
    const canonicalId = value.startsWith(CANONICAL_PREFIX) ? value.slice(CANONICAL_PREFIX.length) : ''
    const candidateKey = value.startsWith(IDENTITY_PREFIX) ? value.slice(IDENTITY_PREFIX.length) : ''
    const candidate = discoveredCandidates.find((item) => item.key === candidateKey)
    const identifier = candidate?.identifiers[0]
    setData('person', {
      ...data.person,
      token_usage_person_id: canonicalId,
      target_channel: identifier?.channel ?? '',
      target_person_ref_hash: identifier?.person_ref_hash ?? '',
      target_person_ref_type: identifier?.person_ref_type ?? '',
    })
  }

  const createsCanonicalPerson = !targetValue.startsWith(CANONICAL_PREFIX)

  return (
    <Dialog open={Boolean(person)} onOpenChange={(open) => { if (!open) close() }}>
      <DialogContent className="rounded-xl border-border bg-card sm:max-w-lg">
        {mapped && person ? (
          <MappedPersonEditor workerId={workerId} person={person} canonicalPeople={canonicalPeople} copy={copy} onClose={close} />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-[16px]">{copy.people.dialog_title}</DialogTitle>
              <DialogDescription className="text-[13px]">{copy.people.dialog_description}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {hasGroupingCandidates && (
                <div className="space-y-2">
                  <Label htmlFor="token-usage-person-group">{copy.people.group_label}</Label>
                  <Select value={targetValue} onValueChange={handleTargetChange}>
                    <SelectTrigger id="token-usage-person-group" className="w-full"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={CREATE_NEW}>{copy.people.create_new}</SelectItem>
                      {canonicalPeople.map((item) => (
                        <SelectItem key={item.id} value={`${CANONICAL_PREFIX}${item.id}`}>
                          <span className="flex min-w-0 items-center gap-2">
                            <span className="truncate">{item.name}</span>
                            <span className="flex shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                              {item.channels.map((channel) => (
                                <span key={channel} className="inline-flex items-center gap-1">
                                  <UsageChannelIcon channel={channel} />
                                  {channel}
                                </span>
                              ))}
                            </span>
                          </span>
                        </SelectItem>
                      ))}
                      {discoveredCandidates.map((item) => (
                        <SelectItem key={item.key} value={`${IDENTITY_PREFIX}${item.key}`}>
                          <span className="flex min-w-0 items-center gap-2">
                            <span className="truncate">{item.name}</span>
                            <span className="flex shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                              {item.raw_channels.map((channel, index) => (
                                <span key={channel} className="inline-flex items-center gap-1">
                                  <UsageChannelIcon channel={channel} />
                                  {item.channels[index] ?? channel}
                                </span>
                              ))}
                            </span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {createsCanonicalPerson && (
                <div className="space-y-2">
                  <Label htmlFor="token-usage-person-name">{copy.people.alias_label}</Label>
                  <Input id="token-usage-person-name" value={data.person.name} onChange={(event) => setData('person', { ...data.person, name: event.target.value })} placeholder={copy.people.alias_placeholder} autoFocus />
                </div>
              )}
            </div>

            <DialogFooter className="gap-2 sm:space-x-0">
              <Button type="button" variant="ghost" onClick={close}>{copy.people.cancel}</Button>
              <Button type="button" onClick={submit} disabled={processing || (createsCanonicalPerson && !data.person.name.trim())}>{copy.people.save}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
